import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { ItineraryDay, JournalEntry, TripPhoto } from '../../types';
import { useDateLocale } from '../../utils/dateLocale';
import { Icon, Pencil } from '../ui/Icon';
import JournalEntryCard from './JournalEntryCard';
import JournalEntryForm from './JournalEntryForm';
import PhotoGrid from './PhotoGrid';
import PhotoMosaic from './PhotoMosaic';
import TimelineDay from '../ui/TimelineDay';

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; entryId: string };

interface Props {
  day: ItineraryDay;
  /** Where the trip is that day (ADR-0030), when it has several destinations. */
  place?: string | null;
  entries: JournalEntry[];
  /** Every photo on this day, chosen by an entry or not. */
  photos: TripPhoto[];
  /** All days at once: a day on the timeline with thumbnails; the open day gets the mosaic. */
  overview?: boolean;
  /** The last day shown ends the timeline's rail. */
  last?: boolean;
  meId: string | undefined;
  canEdit: boolean;
  isOwner: boolean;
  onCreate: (dayId: string, body: string, photoIds: string[], shared: boolean) => Promise<void>;
  onUpdate: (entryId: string, body: string, photoIds: string[], shared: boolean) => Promise<void>;
  onDeleteEntry: (entry: JournalEntry) => void;
  onUpload: (files: File[], dayId: string) => Promise<TripPhoto[]>;
  onOpenPhoto: (photo: TripPhoto, among: TripPhoto[]) => void;
}

export default function JournalDaySection({
  day, place, entries, photos, overview = false, last = false, meId, canEdit, isOwner, onCreate, onUpdate, onDeleteEntry, onUpload, onOpenPhoto,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  // Photos uploaded from the form may be placed on another day by their date
  // (ADR-0042); keep them choosable here until the entry pulls them in.
  const [justUploaded, setJustUploaded] = useState<TripPhoto[]>([]);

  const byId = useMemo(() => new Map([...photos, ...justUploaded].map(p => [p.id, p])), [photos, justUploaded]);
  const loose = useMemo(() => photos.filter(p => !p.journalEntryId), [photos]);

  function choosable(entryId: string | null): TripPhoto[] {
    const own = [...byId.values()].filter(p => !p.journalEntryId || p.journalEntryId === entryId);
    return own.sort((a, b) => (a.takenAt ?? a.createdAt).localeCompare(b.takenAt ?? b.createdAt));
  }

  async function upload(files: File[]) {
    const stored = await onUpload(files, day.id);
    setJustUploaded(prev => [...prev, ...stored]);
    return stored;
  }

  function close() {
    setMode({ kind: 'idle' });
    setJustUploaded([]);
  }

  const pencil = canEdit && mode.kind === 'idle' && (
    <button
      onClick={() => setMode({ kind: 'new' })}
      className="btn-icon w-7 h-7 text-slate-400 hover:text-teal-600 shrink-0"
      aria-label={t('journal.write')}
      title={t('journal.write')}
    >
      <Icon as={Pencil} size={14} />
    </button>
  );
  const content = (
    <>
      {overview
        ? <PhotoGrid photos={loose} size="thumb" onOpen={photo => onOpenPhoto(photo, loose)} />
        : <PhotoMosaic photos={loose} onOpen={photo => onOpenPhoto(photo, loose)} />}

      {entries.map(entry => mode.kind === 'edit' && mode.entryId === entry.id ? (
        <JournalEntryForm
          key={entry.id}
          photos={choosable(entry.id)}
          initialBody={entry.body}
          initialPhotoIds={entry.photoIds}
          initialShared={entry.shared}
          onSubmit={async (body, ids, shared) => { await onUpdate(entry.id, body, ids, shared); close(); }}
          onCancel={close}
          onUpload={upload}
        />
      ) : (
        <JournalEntryCard
          key={entry.id}
          entry={entry}
          photos={entry.photoIds.map(id => byId.get(id)).filter((p): p is TripPhoto => !!p)}
          canEditEntry={entry.author.id === meId}
          canDeleteEntry={canEdit && (entry.author.id === meId || isOwner)}
          onEdit={() => setMode({ kind: 'edit', entryId: entry.id })}
          onDelete={() => onDeleteEntry(entry)}
          photoSize={overview ? 'thumb' : 'compact'}
          onOpenPhoto={photo => onOpenPhoto(photo, entry.photoIds.map(id => byId.get(id)).filter((p): p is TripPhoto => !!p))}
        />
      ))}

      {mode.kind === 'new' && (
        <JournalEntryForm
          photos={choosable(null)}
          onSubmit={async (body, ids, shared) => { await onCreate(day.id, body, ids, shared); close(); }}
          onCancel={close}
          onUpload={upload}
        />
      )}
    </>
  );

  // The tickets' chronology header, in the overview and on the open day alike.
  return (
    <TimelineDay
      number={day.dayNumber}
      filled={photos.length > 0 || entries.length > 0}
      last={last}
      header={<>
        <h3 className="font-sans text-xs font-semibold text-slate-500 dark:text-slate-400">{t('gallery.dayOption', { number: day.dayNumber })}</h3>
        <span className="text-xs text-slate-400 dark:text-slate-500">{format(parseISO(day.date), 'EEE d MMM', { locale: dateLocale })}</span>
        {place && <span className="text-xs text-slate-400 dark:text-slate-500 truncate">· {place}</span>}
        {pencil}
      </>}
    >
      <div className="space-y-3">
        {!overview && day.title && <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{day.title}</p>}
        {content}
      </div>
    </TimelineDay>
  );
}
